import { Button, Flex, Label, ModalManagerContext, Text, Image, Grid } from '@pega/cosmos-react-core';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import { ConfirmationAlert } from '../../Utilities/CustomModal';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import { deleteAccount, getAccountDetails } from '../ABC/AbcApis';
import * as Logos from '../../../constants/images';
import { appState, confirmationAlertProps } from '../../../interfaces/common';
import useLocalTranslation from '../../../hooks/useLocalTranslation';

type AccountDetails = {
  name: string;
  businessChatId: string;
};

const ABC: React.FC<any> = (props) => {
  const { id: connectionId }: any = useParams();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const initialState: AccountDetails = { name: '', businessChatId: '' };
  const [accountDetails, setaccountDetails] = useState(initialState);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const deleteConfirmation = () => {
    modalMethods.current = create(
      ConfirmationAlert,
      { pageName: accountDetails?.name, deleteChannel: deleteChannel },
      { alert: true },
    );
  };

  const fetchAccountDetails = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await getAccountDetails(sessionId, connectionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      setaccountDetails(response?.data);
    } else {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, connectionId, navigate]);

  const deleteChannel = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await deleteAccount(connectionId, sessionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      navigate('/');
      dispatch(
        updateBannerInfo(`${translate('Apple Messages for Business')} ${translate('account_deleted')}`, 'success'),
      );
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    } else {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
  }, [dispatch, connectionId, sessionId, navigate, translate]);

  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  const headerElement = () => {
    return (
      accountDetails?.name && (
        <Text variant="h1" as="h1">
          {accountDetails?.name}
        </Text>
      )
    );
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      {accountDetails?.name && (
        <Flex container={{ direction: 'column', itemGap: 1 }} item={{ grow: 1 }}>
          <Grid
            container={{
              cols: 'repeat(3, 1fr)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('connection')}</Label>
            <Flex
              container={{
                direction: 'row',
                itemGap: 1,
                alignItems: 'center',
              }}
            >
              <Text variant="secondary">{translate('ABC')}</Text>
              <Image alt="Twitter" src={Logos.ABCLogo} width="30px" height="40px" />
            </Flex>
          </Grid>

          <Grid
            container={{
              cols: 'repeat(2, 33% 67%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('business_chat_id')}</Label>
            <Text variant="secondary">{accountDetails?.businessChatId}</Text>
          </Grid>

          <Flex
            container={{
              direction: 'column',
              itemGap: 1,
            }}
          >
            <Flex style={{ paddingTop: '1.5rem' }}>
              <Button onClick={deleteConfirmation}>{translate('delete_connection_button')}</Button>
            </Flex>
          </Flex>
        </Flex>
      )}
    </SectionRenderTemplate>
  );
};

export default ABC;
